import React, { useState, Fragment } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import CloseRounded from '@mui/icons-material/CloseRounded';

import styled from '@emotion/styled';
import { DialogFormProvider, useDialogForm } from '../../contexts/DialogForm';
import { BasicFlex } from '../Common';
import useIsMobile from '../../hooks/useIsMobile';

import { IconButton } from '@mui/material';

const MainContainer = styled(BasicFlex)`
  height: 100%;
  align-items: center;
`;

const Arrow = styled(BasicFlex)`
  height: 100%;
  width: 4rem;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  /* Disable arrow if at bounds */
  opacity: ${props => props.disabled ? 0.3 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`;

const MediaDisplay = styled(BasicFlex)`
  flex-direction: column; // Stack media and caption
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  min-height: 300px;
  position: relative;
  
  img, video {
    object-fit: contain;
    max-width: 100%;
    max-height: 70vh;
  }
`;

const Caption = styled.div`
  padding: 8px;
  color: #666;
  font-size: 1.2rem;
  text-align: center;

  .counter {
    margin-top: 1rem;
    font-size: 1.8rem;
  }
`;

const Gallery = ({ title, size = "md", cancelText = "Close", forceOpen = false, forceClose = () => {}, toggle, images = [], imageIndex = 0, ...props }) => {
  const [indexOfImage, setIndexOfImage] = useState(imageIndex);
  const { isMobile } = useIsMobile();
  const { open, handleClickOpen, handleClose } = useDialogForm({ forceOpen, forceClose });

  const selectedImage = images[indexOfImage] || {};
  const { originalname, signedUrlKey, signedUrl: thumbnail, label } = selectedImage;

  // console.log(selectedImage, "seelelelelelel")

  const isVideo = signedUrlKey?.match(/\.(mp4|webm|ogg|mov|m4v)$/i) || originalname?.match(/\.(mp4|webm|ogg|mov|m4v)$/i);

  const handleNext = () => {
    if (indexOfImage < images.length - 1) setIndexOfImage(indexOfImage + 1);
  };

  const handlePrev = () => {
    if (indexOfImage > 0) setIndexOfImage(indexOfImage - 1);
  };

  return (
    <Fragment>
      <div onClick={handleClickOpen}>{toggle}</div>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={size}
        fullWidth
        fullScreen={isMobile}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        <DialogTitle sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
          {title || "Gallery"} 
          <IconButton color="basic" onClick={handleClose}><CloseRounded /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ padding: isMobile ? 1 : 2 }} onClick={(e) => e.stopPropagation()}>
          <MainContainer>
            <Arrow onClick={handlePrev} disabled={indexOfImage === 0}>
              <Avatar><ArrowBackRoundedIcon /></Avatar>
            </Arrow>

            {images.length > 0 && (
              <MediaDisplay>
                {isVideo ? (
                  <video 
                    key={signedUrlKey} 
                    src={signedUrlKey} 
                    poster={thumbnail} 
                    controls 
                    // autoPlay 
                    playsInline // Necessary for iOS autoplay/inline play
                    style={{ backgroundColor: '#000' }} // Standard for video players
                  />
                ) : (
                  <img src={signedUrlKey} alt={label || originalname} />
                )}
                
                {/* Caption Overlay */}
                <Caption>
                   <strong>{label || originalname}</strong> 
                   <div className='counter'>{indexOfImage + 1} of {images.length}</div>
                </Caption>
              </MediaDisplay>
            )}

            <Arrow onClick={handleNext} disabled={indexOfImage === images.length - 1}>
              <Avatar><ArrowForwardRoundedIcon /></Avatar>
            </Arrow>
          </MainContainer>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleClose}>{cancelText}</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default function GalleryComponent(props) {
  return (
    <DialogFormProvider forceClose={props.forceClose} forceOpen={props.forceOpen}>
      <Gallery {...props} />
    </DialogFormProvider>
  );
}